import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCustomers, exportCustomers } from "../../api/customer";
import { MdOutlineArrowLeft, MdOutlineArrowRight, MdOutlineListAlt } from "react-icons/md";
import { AiOutlineExport } from "react-icons/ai";
import DynamicSVG from "../../components/dashboard/venue-table/dynmaic-svg";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { CiFilter } from "react-icons/ci";
import { useNavigate } from "react-router-dom";


export default function Customers() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showDialog, setShowDialog] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [formErrors, setFormErrors] = useState({ fromDate: "", toDate: "" });
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [filterField, setFilterField] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  const [filterQuery, setFilterQuery] = useState( { criteria: [] });
  const [searchQuery, setSearchQuery] = useState(null);
  
  const popoverRef = useRef(null);

  const { data, isLoading,refetch } = useQuery({
    queryKey: ["customersData", currentPage, itemsPerPage],
    queryFn: async () => {
      return await getCustomers(currentPage, itemsPerPage, filterQuery, searchQuery);
    },
    refetchOnWindowFocus: false,
  });

  const totalPages = Math.ceil((data?.total || 0) / itemsPerPage);

  useEffect(()=> {
    if(filterField && filterValue) {
      setFilterQuery({ criteria: [{ key: filterField, value: filterValue, type: "regexOr"}] })
    }
    else {
      setFilterQuery({ criteria: [] });
      refetch();
    }
  },[filterValue])

  useEffect(() => {
    refetch();
  },[searchQuery])

  useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(event.target)
        ) {
          setShowPopover(false);
        }
      };
  
      if (showPopover) {
        document.addEventListener("mousedown", handleClickOutside);
      }
  
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
  }, [showPopover]);

  function getBooleanBadge(value) {
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold rounded-md border ${
          value
            ? "bg-green-100 text-green-700 border-green-500"
            : "bg-rose-100 text-rose-700 border-rose-500"
        }`}
      >
        {value ? "Verified" : "Un Verified"}
      </span>
    );
  }

  function getSelectBadge(value) {
    let color = "bg-gray-500";
    if (value === "corporate") color = "bg-cyan-600";
    if (value === "eventPlanner") color = "bg-amber-500";
    if (value === "individual") color = "bg-violet-500";

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${color}`}>
        {value}
      </span>
    );
  }

  const handleExport = async () => {
    const errors = {
      fromDate: fromDate ? "" : "From Date is required.",
      toDate: toDate ? "" : "To Date is required.",
    };
    setFormErrors(errors);

    if (!fromDate || !toDate) return;

    try {
      const exportData = await exportCustomers({ fromDate, toDate });
      const worksheet = XLSX.utils.json_to_sheet(exportData.data || []);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const fileBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(fileBlob, `CustomerExport_${fromDate}_to_${toDate}.xlsx`);
      setShowDialog(false);
      setFormErrors({ fromDate: "", toDate: "" });
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const actionBodyTemplate = (rowData) => (
    <button
      onClick={() => navigate(`/customer-enquiries/${rowData._id}`, { state: { customerName: rowData.fullName } })}
      title="View Enquiries"
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition"
    >
      <MdOutlineListAlt className="text-base" />
      Enquiries
    </button>
  );

  	const handleFilterPopover = (event, field) => {
      const rect = event.target.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setShowPopover(true);
      setFilterField(field);
      setFilterValue(null);
	};


  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary px-4">Customers</h2>
        <div className="flex items-center gap-4">
          <input
            type="text"
            id="Search"
            placeholder="Search for..."
            className="w-full rounded-md py-2 outline-none ps-16 text-sm h-full"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
					/>
          <button
            onClick={() => setShowDialog(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow text-sm"
          >
            <AiOutlineExport className="text-lg" />
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable value={data?.data} loading={isLoading} stripedRows responsiveLayout="scroll">
        <Column
          header="SNo"
          body={(_, options) => (
            <DynamicSVG index={options.rowIndex} currentPage={currentPage} itemsPerPage={itemsPerPage} />
          )}
        />
        <Column field="fullName" 
        header={
        <div className="flex items-center gap-2 px-2" onClick={(e) => handleFilterPopover(e, "fullName")}>
          Full Name <CiFilter className="ps-2 w-6 h-6 text-gray-600" />
          </div>} 
        />
        <Column field="email" header={
          <div className="flex items-center gap-2 px-2" onClick={(e) => handleFilterPopover(e, "email")}>
          Email <CiFilter className="ps-2 w-6 h-6 text-gray-600" />
          </div>} 
        />
        <Column
          field="select"
          header={
          <div className="flex items-center gap-2 px-2" onClick={(e) => handleFilterPopover(e, "select")}>
          Type <CiFilter className="ps-2 w-6 h-6 text-gray-600" />
          </div>} 
          body={(rowData) => getSelectBadge(rowData.select)}
        />
        <Column field="mobileNumber" header={
          <div className="flex items-center gap-2 px-2" onClick={(e) => handleFilterPopover(e, "mobileNumber")}>
          Mobile Number <CiFilter className="ps-2 w-6 h-6 text-gray-600" />
          </div>}  />
        <Column
          field="isVerified"
          header={
          <div className="flex items-center gap-2 px-2" onClick={(e) => handleFilterPopover(e, "isVerified")}>
          Is Verified 
          {/* <CiFilter className="ps-2 w-6 h-6 text-gray-600" /> */}
          </div>} 
          body={(rowData) => getBooleanBadge(rowData.isVerified)}
        />
        <Column
          field="createdAt"
          header="Joined"
          body={(rowData) => moment(rowData.createdAt).format("DD-MM-YYYY")}
        />
        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ textAlign: "center", width: "9rem" }}
        />
      </DataTable>

      {/* Pagination */}
      <div className="flex justify-center items-center gap-5 mt-6">
        <span className="text-black text-sm">Page</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => prev - 1)}
            disabled={currentPage === 1}
            className={`disabled:opacity-50 ${currentPage === 1 ? "cursor-not-allowed" : ""}`}
          >
            <MdOutlineArrowLeft className="text-primary text-2xl" />
          </button>
          <div className="min-w-[32px] h-[32px] flex bg-primary text-white justify-center items-center rounded-full text-sm font-bold">
            {currentPage}
          </div>
          <button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={currentPage === totalPages}
            className={`disabled:opacity-50 ${currentPage === totalPages ? "cursor-not-allowed" : ""}`}
          >
            <MdOutlineArrowRight className="text-primary text-2xl" />
          </button>
        </div>

        <select
          value={itemsPerPage}
          onChange={(e) => {
            setCurrentPage(1);
            setItemsPerPage(parseInt(e.target.value));
          }}
          className="py-2 px-3 bg-white border border-blue-200 rounded text-sm"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
        </select>
      </div>

      {/* Export Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
          <div className="bg-white rounded-xl p-6 w-[90%] sm:w-[400px] shadow-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Export Customers</h3>
            <p className="text-sm text-gray-600 mb-3">Select a date range to export customer data.</p>
            <div className="flex flex-col gap-4 mb-5">
              <div>
                <label className="text-sm font-medium">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    if (e.target.value) {
                      setFormErrors((prev) => ({ ...prev, fromDate: "" }));
                    }
                  }}
                  className={`mt-1 w-full px-3 py-2 border rounded-md ${
                    formErrors.fromDate ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {formErrors.fromDate && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.fromDate}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    if (e.target.value) {
                      setFormErrors((prev) => ({ ...prev, toDate: "" }));
                    }
                  }}
                  className={`mt-1 w-full px-3 py-2 border rounded-md ${
                    formErrors.toDate ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {formErrors.toDate && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.toDate}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDialog(false)}
                className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={!fromDate || !toDate}
                className={`px-4 py-2 text-sm text-white rounded ${
                  !fromDate || !toDate
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}

      {
        showPopover && (
          <div
            ref={popoverRef}
            className="fixed z-[999] bg-white border rounded-xl shadow-2xl w-60 max-h-100 overflow-y-auto py-4 px-4 space-y-4"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
          >
            <input
              className="w-full px-4 py-2 rounded-md border bg-white text-gray-800 placeholder-gray-400 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
              active:ring-2 active:ring-blue-500 transition duration-200"
              placeholder={filterField}
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            />

            <div className="flex justify-end gap-2 pt-2">
              <button
                className="px-4 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                onClick={() => {
                  setFilterValue("");
                  setFilterQuery({ criteria: [] })
                  refetch();
                }}
              >
                Clear
              </button>
              <button
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
                onClick={() => refetch()}
              >
                Apply
              </button>
            </div>
          </div>
        )
      }
    </div>
  );
}
